// Shared by appleSignIn.web.ts and googleSignIn.web.ts only — native has no
// popup to hang on (native Google/Apple SDKs have their own OS-level
// cancellation), so this has no native counterpart and isn't imported from
// anywhere native-reachable.
//
// Firebase JS SDK's signInWithPopup() has no timeout of its own: the
// returned promise settles only when the popup completes the OAuth redirect
// or when the SDK's own polling notices `popup.window.closed === true`
// (firebase/auth's PopupOperation.pollUserCancellation). If the popup
// window is silently blocked without a synchronous "popup-blocked" throw,
// or `.closed` becomes unreadable (e.g. a strict Cross-Origin-Opener-Policy
// on the host page), that promise never resolves or rejects — reproduced
// live: the login screen's "Finding your vibe…" splash stayed up forever
// with zero console output. Racing the real sign-in against a hard
// client-side timeout guarantees the caller's promise always settles.
const OAUTH_POPUP_TIMEOUT_MS = 45_000;

export class OAuthPopupTimeoutError extends Error {
  code = 'tonyte/popup-timeout';
  constructor() {
    super('OAuth popup sign-in timed out');
    this.name = 'OAuthPopupTimeoutError';
  }
}

/**
 * Races `promise` against a timeout, rejecting with OAuthPopupTimeoutError
 * if it wins. Always clears the timer once the race settles either way, and
 * attaches a no-op catch to `promise` itself so a LATE rejection (the
 * original popup finally closing after the timeout already fired and the UI
 * moved on) never surfaces as an unhandled promise rejection.
 */
export function withPopupTimeout<T>(promise: Promise<T>): Promise<T> {
  promise.catch(() => undefined);

  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new OAuthPopupTimeoutError()), OAUTH_POPUP_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}
